import { NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import {
    RHProcurementOffer,
    RHProcurementPart,
    RHProcurementPartOffer,
    RHProcurementPublic
} from "../../../../models/prodModels"
import { RHCore } from "../../Core/RHCore"
import type { RHRequiredDocument } from "../../../../models/ProcurementModels"
import { RHFormSessionSnapshotStore } from "../../Core/RHFormSessionSnapshotStore"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHPanelView } from "../../Custom components/RH/RHPanelView"
import { RHButtons, RHColors, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurementPartDisplay } from "../../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHProcurementStatusPresentation } from "../../Custom components/RH/Utils_slop/RHProcurementStatusPresentation"
import { RHMakeOfferViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { RHProcurementPartView } from "../RHProcurementPartView"
import { EnterProcurementItemOfferView, RHOfferItemFormSessionSnapshot } from "./EnterProcurementItemOfferView"
import { messageOfError } from "../../Core/RHErrorMessage"


type RHOfferFormSessionSnapshot = {
    procurementId: string
    partOffers: RHOfferItemFormSessionSnapshot[]
}

type RHOfferDraftSaveResult = {
    procurement: RHProcurementPublic
    savedDraftOffer: RHProcurementOffer
    offersByPartId: Map<string, RHProcurementPartOffer[]>
}


// ─────────────────────────────────────────────────────────────────────────────
// One section per procurement part: part header + its offer card(s)
// ─────────────────────────────────────────────────────────────────────────────

class PartOfferSection extends RHPanelView {
    
    readonly partView: RHProcurementPartView
    readonly offerCards: EnterProcurementItemOfferView[] = []
    
    readonly addOfferButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "+ Lisa pakkumus" },
            variant: RHButtons.compactPrimaryVariant,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: {
                fontSize: RHButtons.compactPrimaryFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => { this._addNewOfferCard() }
            }
        })
        .addedAsSubviewToView(this)
    
    private _part: RHProcurementPart
    _procurementId: string
    private _partCount: number
    private _maxOffers: number | null | undefined
    private _isFinalized = false
    private _offerActionsEnabled = YES
    _requiredDocumentsJSON: string
    _isClosedWithoutContract: boolean
    
    sectionDidChangeState?: () => void
    
    override _focusManager = new RHFocusManager(() => {
        const items: (UIView | RHFocusManager)[] = []
        for (const card of this.offerCards) {
            if (card._focusManager) {
                items.push(card._focusManager)
            }
        }
        if (this.addOfferButton.superview === this) {
            items.push(this.addOfferButton)
        }
        return items as UIView[]
    })
    
    constructor(part: RHProcurementPart, procurement: RHProcurementPublic) {
        super()
        // White card per part, mirroring the procurer's per-part evaluation card
        // (RHPartOfferEvaluationSection) and the offerer's OfferPartCardView.
        this.backgroundColor = UIColor.whiteColor
        this._part = part
        this._procurementId = procurement._id?.toString() ?? ""
        this._partCount = procurement.parts.length
        this._maxOffers = procurement.maxOffersPerPart
        this._requiredDocumentsJSON = JSON.stringify(part.requiredDocuments ?? [])
        
        const isClosedWithoutContract = (procurement.closedWithoutContractPartIds ?? [])
            .includes(part._id.toString())
        this._isClosedWithoutContract = isClosedWithoutContract
        this.partView = new RHProcurementPartView()
            .addedAsSubviewToView(this) as RHProcurementPartView
        this.partView.configureWithPartData(part, YES, undefined, this._partCount, isClosedWithoutContract)
        if (!RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(this._partCount)) {
            this.partView.partHeaderLabel.text = "Pakkumus"
            this.partView.partHeaderLabel.hidden = NO
        }
    }

    get offerActionsEnabled(): boolean {
        return this._offerActionsEnabled
    }

    get partId(): string {
        return this._part._id.toString()
    }

    set offerActionsEnabled(enabled: boolean) {
        this._offerActionsEnabled = enabled
        this.addOfferButton.enabled = enabled
        this._refreshAddButton()
    }

    get isFinalized(): boolean {
        return this._isFinalized
    }

    set finalized(finalized: boolean) {
        this._isFinalized = finalized
        this.addOfferButton.removeFromSuperview()
    }
    
    async addExistingOfferCard(partOffer: RHProcurementPartOffer) {
        const card = this._createCard()
        await card.configureForPart(this._part, partOffer, !this._isFinalized, this._partCount)
        this._refreshAddButton()
        this.setNeedsLayoutUpToRootView()
    }
    
    addEmptyOfferCard() {
        const card = this._createCard()
        card.configureForPart(this._part, undefined, !this._isFinalized, this._partCount)
        this._refreshAddButton()
        this.setNeedsLayoutUpToRootView()
    }

    sessionSnapshots(): RHOfferItemFormSessionSnapshot[] {
        return this.offerCards.map(card => card.sessionSnapshot())
    }

    updateRequiredDocuments(documents: RHRequiredDocument[]): { hasRequiredDocumentChanges: boolean } {
        const documentsJSON = JSON.stringify(documents)
        if (documentsJSON === this._requiredDocumentsJSON) {
            return { hasRequiredDocumentChanges: NO }
        }
        this._requiredDocumentsJSON = documentsJSON
        this._part = { ...this._part, requiredDocuments: documents }
        this.partView.configureWithPartData(this._part, YES, undefined, this._partCount, this._isClosedWithoutContract)
        if (!RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(this._partCount)) {
            this.partView.partHeaderLabel.text = "Pakkumus"
            this.partView.partHeaderLabel.hidden = NO
        }
        for (const card of this.offerCards) {
            card.updateRequiredDocuments(documents)
        }
        return { hasRequiredDocumentChanges: YES }
    }

    async addSessionSnapshot(snapshot: RHOfferItemFormSessionSnapshot) {
        const card = this._createCard()
        await card.configureForPart(this._part, undefined, !this._isFinalized, this._partCount)
        card.restoreSessionSnapshot(snapshot)
        this._refreshAddButton()
        this.setNeedsLayoutUpToRootView()
    }
    
    partOfferInputs(): Array<{
        partId: string
        price: { amount: number; currency: string }
        fileIds?: string[]
        requiredDocumentFileIds?: RHProcurementPartOffer["requiredDocumentFileIds"]
    }> {
        return this.offerCards
            .filter(card => card.hasPrice)
            .map(card => ({
                partId: card.partId!,
                price: { amount: card.priceValue!, currency: "EUR" },
                fileIds: card.optionalFileIds,
                requiredDocumentFileIds: card.requiredDocumentFileIds
            }))
    }
    
    /**
     * Returns an error message string if any required document slot across all
     * offer cards in this section is missing a file, or null if everything is
     * present.
     */
    requiredDocumentValidationError(): string | null {
        for (const card of this.offerCards) {
            if (!card.hasPrice) {
                continue
            }
            for (const slot of card._documentRequirementSlots) {
                if (slot.isRequired && slot.uploadView.uploadedFiles.length === 0) {
                    const prefix = RHProcurementPartDisplay.requiredDocumentValidationPrefix(
                        this._part,
                        this._partCount
                    )
                    return `${prefix}Nõutud dokument "${slot.document.name}" on üles laadimata`
                }
            }
        }
        return null
    }

    /**
     * One waitForPendingUploads() promise per upload zone across this
     * section's cards (the general offer-file zone and every required-
     * document slot) - callers await all of them together before saving, so
     * a file that's still mid-upload has landed in uploadedFiles (or failed
     * and been reported) by the time validation/save actually runs.
     */
    pendingUploadCompletions(): Promise<boolean>[] {
        return this.offerCards.flatMap(card => [
            card.fileUploadView.waitForPendingUploads(),
            ...card._documentRequirementSlots.map(slot => slot.uploadView.waitForPendingUploads())
        ])
    }

    private _createCard(): EnterProcurementItemOfferView {
        const card = new EnterProcurementItemOfferView()
            .addedAsSubviewToView(this) as EnterProcurementItemOfferView
        card.configureForOffer({ procurementId: this._procurementId })
        card.offerItemDidChange = () => { this.sectionDidChangeState?.() }
        card.didRequestDelete = () => { this._deleteCard(card) }
        this.offerCards.push(card)
        return card
    }
    
    private _deleteCard(card: EnterProcurementItemOfferView) {
        card.removeFromSuperview()
        const index = this.offerCards.indexOf(card)
        if (index !== -1) { this.offerCards.splice(index, 1) }
        this._refreshAddButton()
        this.sectionDidChangeState?.()
    }
    
    private _addNewOfferCard() {
        this.addEmptyOfferCard()
        this.sectionDidChangeState?.()
    }
    
    private _addOfferButtonWidth(availableWidth: number): number {
        return [
            [
                this.addOfferButton.titleLabel.intrinsicContentWidth(RHButtons.compactPrimaryHeight) +
                RHButtons.compactPrimaryContentPadding * 2 +
                RHSpacing.large,
                240
            ].max(),
            320,
            availableWidth
        ].min()
    }
    
    private _refreshAddButton() {
        if (this._isFinalized) {
            this.addOfferButton.removeFromSuperview()
            return
        }
        const max = this._maxOffers
        const count = this.offerCards.length
        const shouldShow = typeof max === "number"
                           ? (max === 0 || count < max)
                           : count < 1
        
        if (shouldShow) {
            this.addOfferButton.enabled = this._offerActionsEnabled
            this.addOfferButton.addedAsSubviewToView(this)
        }
        else {
            this.addOfferButton.removeFromSuperview()
        }
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(padding)

        bounds
            .rectangleWithHeight(this.partView)
            .assignedAsFrameOfView(this.partView)
        
        const afterCards = this.offerCards.length > 0
                           ? this.partView.frame
                               .rectangleForNextRow(padding * 0.5)
                               .framesByDistributingViewsAsColumn(this.offerCards, padding * 0.5)
                               .lastElement
                           : this.partView.frame
        
        if (this.addOfferButton.superview === this) {
            const addOfferButtonRowFrame = afterCards.rectangleForNextRow(
                padding * 0.5,
                RHButtons.compactPrimaryHeight
            )
            const addOfferButtonWidth = this._addOfferButtonWidth(addOfferButtonRowFrame.width)
            this.addOfferButton.frame = addOfferButtonRowFrame.rectangleWithWidth(addOfferButtonWidth, 0.5)
        }
    }
}


// ─────────────────────────────────────────────────────────────────────────────
// Step 1: data entry
// Saves draft and calls didSaveDraftAndProceedToConfirm — MakeOfferView handles switching.
// ─────────────────────────────────────────────────────────────────────────────

export class EnterProcurementOfferView extends UIView {
    
    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Sinu pakkumused",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textPrimary
        })
    
    private _partSections: PartOfferSection[] = []
    
    readonly errorBanner = new RHAlertBannerView()
        .configuredWithObject({
            alertType: RHAlertBannerView.type.Error
        })

    readonly saveButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: RHMakeOfferViewTokens.saveButtonTitle },
            variant: RHButtons.compactPrimaryVariant,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: {
                fontSize: RHButtons.compactPrimaryFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this.saveDraftAndNavigateToDashboard()
                }
            }
        })
    
    readonly proceedButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: RHMakeOfferViewTokens.proceedButtonTitle },
            variant: "success",
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: {
                fontSize: RHButtons.compactPrimaryFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this.saveDraftAndProceedToConfirmation(this.proceedButton)
                }
            }
        })

    deleteDraftOfferButton?: RHButton
    
    readonly lockedMessageLabel = new RHAlertBannerView()
        .configuredWithObject({
            text: "🔒 Hange on lõppenud. Pakkumuste esitamine ei ole enam võimalik.",
            alertType: RHAlertBannerView.type.Warning,
            textPresentation: RHAlertBannerView.textPresentation.Outcome
        })
    
    /**
     * Called when the draft has been saved and the user is ready to proceed.
     * MakeOfferView listens here to swap in ConfirmProcurementOfferView.
     */
    didSaveDraftAndProceedToConfirm?: (
        procurement: RHProcurementPublic,
        savedDraftOffer: RHProcurementOffer,
        offersByPartId: Map<string, RHProcurementPartOffer[]>
    ) => Promise<void> | void

    didSaveDraftAndNavigateToDashboard?: () => Promise<void> | void
    
    private _procurement?: RHProcurementPublic
    private _isProcurementFinalized = false
    private _isSaving = false
    private _offerActionsEnabled = YES
    _formStateIsBeingApplied = NO
    _formLoadGeneration = 0
    _draftSaveGeneration = 0
    
    override _focusManager = new RHFocusManager(() => {
        const items: (UIView | RHFocusManager)[] = []
        for (const section of this._partSections) {
            if (section._focusManager) {
                items.push(section._focusManager)
            }
        }
        if (this.saveButton.superview === this) {
            items.push(this.saveButton)
        }
        if (this.proceedButton.superview === this) {
            items.push(this.proceedButton)
        }
        if (this.deleteDraftOfferButton?.superview === this && !this.deleteDraftOfferButton.hidden) {
            items.push(this.deleteDraftOfferButton)
        }
        return items as UIView[]
    })
    
    constructor(elementID?: string) {
        super(elementID)
        // No panel chrome on the outer container — the per-part white cards stand on the
        // page directly, matching the procurer's transparent RHOffersEvaluationView.
        this.backgroundColor = UIColor.transparentColor
    }

    get offerActionsEnabled(): boolean {
        return this._offerActionsEnabled
    }

    set offerActionsEnabled(enabled: boolean) {
        this._offerActionsEnabled = enabled
        for (const section of this._partSections) {
            section.offerActionsEnabled = enabled
        }
        this.setNeedsLayoutUpToRootView()
    }

    // ── Public API ────────────────────────────────────────────────────────────
    
    /**
     * Loads (or reloads) the view for a given procurement.
     *
     * @param procurement
     * @param prefillPartOffers  Optional map of partId → RHProcurementPartOffer[]
     *   used to pre-populate the form after a rescind, without hitting the server
     *   for a draft that no longer exists.  When supplied the server draft-fetch
     *   is skipped entirely and each part is pre-filled from this map instead.
     */
    async loadForProcurement(
        procurement: RHProcurementPublic,
        prefillPartOffers?: Map<string, RHProcurementPartOffer[]>
    ) {
        this.cancelPendingSaveIfInvalidForProcurement(procurement)
        const loadGeneration = ++this._formLoadGeneration
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const isCurrentLoad = () => loadGeneration === this._formLoadGeneration &&
            companyId === RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        this._procurement = procurement
        this._isProcurementFinalized = this._checkFinalized()
        this._formStateIsBeingApplied = YES

        try {
            this._clearSections()
            this.subviews.everyElement.removeFromSuperview()
            this._hideErrorBanner()
            this._updateProceedButton()

            if (this._isProcurementFinalized) {
                this.discardSessionSnapshotForProcurement(procurement._id + "")
                this.lockedMessageLabel.addedAsSubviewToView(this)
                this.setNeedsLayout()
                return
            }

            this.titleLabel.addedAsSubviewToView(this)
            this.proceedButton.addedAsSubviewToView(this)
            this.saveButton.addedAsSubviewToView(this)
            if (this.deleteDraftOfferButton) {
                this.deleteDraftOfferButton.addedAsSubviewToView(this)
            }

            // When pre-fill data is provided (post-rescind flow) we skip the
            // server round-trip — there is no draft to fetch anyway.
            const partOffersByPartId = new Map<string, RHProcurementPartOffer[]>()
            let authoritativePartOffersWereFound = NO

            if (prefillPartOffers) {
                authoritativePartOffersWereFound = YES
                for (const [partId, partOffers] of prefillPartOffers) {
                    partOffersByPartId.set(partId, partOffers)
                }
            }
            else {
                const draftOffer = await RHCore.sharedInstance.offerStore.getDraftOfferForProcurement(
                    procurement._id + ""
                )
                if (!isCurrentLoad()) {
                    return
                }

                if (draftOffer?.partOffers?.length) {
                    authoritativePartOffersWereFound = YES
                    for (const partOffer of draftOffer.partOffers as RHProcurementPartOffer[]) {
                        const key = partOffer.partId.toString()
                        if (!partOffersByPartId.has(key)) { partOffersByPartId.set(key, []) }
                        partOffersByPartId.get(key)!.push(partOffer)
                    }
                }
            }

            let formSessionSnapshot: RHOfferFormSessionSnapshot | undefined
            if (authoritativePartOffersWereFound) {
                this.discardSessionSnapshotForProcurement(procurement._id + "")
            }
            else {
                formSessionSnapshot = this._sessionSnapshotForProcurement(procurement._id + "")
                const closedPartIds = [
                    ...(procurement.finalizedPartIds ?? []),
                    ...(procurement.closedWithoutContractPartIds ?? [])
                ]
                if (formSessionSnapshot?.partOffers.some(partOffer => closedPartIds.includes(partOffer.partId))) {
                    this.discardSessionSnapshotForProcurement(procurement._id + "")
                    formSessionSnapshot = undefined
                    this.errorBanner.configureBannerWithObject({
                        alertType: RHAlertBannerView.type.Warning,
                        text: "Pakkumuse koostamine katkestati, sest hankija lõpetas vahepeal hanke osa. " +
                            "Palun alusta pakkumuse koostamist uuesti avatud osadele."
                    })
                    this.errorBanner.addedAsSubviewToView(this)
                }
            }

            for (const part of procurement.parts) {
                const section = this._createSection(part)
                if (section.isFinalized) {
                    continue
                }
                const savedPartOffers = partOffersByPartId.get(part._id.toString()) ?? []
                for (const savedPartOffer of savedPartOffers) {
                    await section.addExistingOfferCard(savedPartOffer)
                    if (!isCurrentLoad()) {
                        return
                    }
                }
                if (savedPartOffers.length === 0 && formSessionSnapshot) {
                    const sessionPartOffers = formSessionSnapshot.partOffers.filter(
                        partOffer => partOffer.partId === part._id.toString()
                    )
                    for (const sessionPartOffer of sessionPartOffers) {
                        await section.addSessionSnapshot(sessionPartOffer)
                        if (!isCurrentLoad()) {
                            return
                        }
                    }
                }
            }

            this._updateProceedButton()
            this.setNeedsLayout()
        }
        catch (error) {
            if (!isCurrentLoad()) {
                return
            }
            console.error("Error loading offers:", error)
            this._showErrorBanner("Viga pakkumuste laadimisel")
        }
        finally {
            if (isCurrentLoad()) {
                this._formStateIsBeingApplied = NO
                this._updateProceedButton()
            }
        }
    }
    
    get hasSections(): boolean {
        return this._partSections.length > 0
    }

    updateRequiredDocumentsForProcurement(procurement: RHProcurementPublic): { hasRequiredDocumentChanges: boolean } {
        if (procurement._id?.toString() !== this._procurement?._id?.toString()) {
            return { hasRequiredDocumentChanges: NO }
        }
        let hasRequiredDocumentChanges = NO
        for (const section of this._partSections) {
            const part = procurement.parts.find(candidate => candidate._id.toString() === section.partId)
            if (part && section.updateRequiredDocuments(part.requiredDocuments ?? []).hasRequiredDocumentChanges) {
                hasRequiredDocumentChanges = YES
            }
        }
        if (hasRequiredDocumentChanges) {
            this.cancelPendingLoad()
            this._draftSaveGeneration += 1
            this._procurement = procurement
            this.errorBanner.configureBannerWithObject({
                alertType: RHAlertBannerView.type.Warning,
                text: "Hanke nõutud dokumente on muudetud. Sinu sisestatud hind ja failid on säilinud. " +
                    "Kontrolli nõudeid enne pakkumuse kinnitamist."
            })
            this.errorBanner.addedAsSubviewToView(this)
            this._saveSessionSnapshot()
            this._updateProceedButton()
            this.setNeedsLayoutUpToRootView()
        }
        return { hasRequiredDocumentChanges }
    }
    
    /**
     * Returns `true` when at least one offer card across all sections has a
     * price entered.  Used by `ProcurementDetailsViewController` to decide
     * whether a navigation-reset guard dialog is needed.
     */
    get hasUnsavedPriceData(): boolean {
        return this._partSections.some(
            section => section.offerCards.some(card => card.hasPrice)
        )
    }

    get hasUnsavedSessionData(): boolean {
        return this._partSections.some(section => section.offerCards.length > 0)
    }

    discardSessionSnapshotForProcurement(procurementId: string) {
        const key = this._formSessionSnapshotKeyForProcurement(procurementId)
        if (key) {
            RHFormSessionSnapshotStore.removeSnapshotForKey(key)
        }
    }

    discardCurrentSessionSnapshot() {
        const procurementId = this._procurement?._id?.toString() ?? ""
        if (procurementId.length > 0) {
            this.discardSessionSnapshotForProcurement(procurementId)
        }
    }

    cancelPendingLoad() {
        this._formLoadGeneration += 1
        this._formStateIsBeingApplied = NO
    }

    cancelPendingSaveIfInvalidForProcurement(procurement: RHProcurementPublic) {
        if (!this._isSaving) {
            return
        }
        const closedPartIds = [
            ...(procurement.finalizedPartIds ?? []),
            ...(procurement.closedWithoutContractPartIds ?? [])
        ]
        const hasOffersForClosedParts = this._partSections.some(section =>
            section.offerCards.some(card => closedPartIds.includes(card.partId ?? ""))
        )
        if (
            procurement._id?.toString() !== this._procurement?._id?.toString() ||
            procurement.status !== "published" ||
            (procurement.deadlineDate && new Date(procurement.deadlineDate) <= new Date()) ||
            hasOffersForClosedParts
        ) {
            // An RPC already sent cannot be recalled; its obsolete completion must not change the view.
            this._draftSaveGeneration += 1
        }
    }

    discardUnsavedChanges() {
        this.cancelPendingLoad()
        this._draftSaveGeneration += 1
        this.discardCurrentSessionSnapshot()
        this._clearSections()
        this._hideErrorBanner()
        this._updateProceedButton()
        this.setNeedsLayoutUpToRootView()
    }
    
    /** Called by MakeOfferView when the user navigates back from the confirm step. */
    restoreForEditing() {
        this.subviews.everyElement.removeFromSuperview()
        this.titleLabel.addedAsSubviewToView(this)
        for (const section of this._partSections) {
            section.addedAsSubviewToView(this)
        }
        this.proceedButton.addedAsSubviewToView(this)
        this.saveButton.addedAsSubviewToView(this)
        if (this.deleteDraftOfferButton) {
            this.deleteDraftOfferButton.addedAsSubviewToView(this)
        }
        this._updateProceedButton()
    }
    
    // ── Private helpers ───────────────────────────────────────────────────────
    
    private _createSection(part: RHProcurementPart): PartOfferSection {
        const section = new PartOfferSection(part, this._procurement!)
        section.offerActionsEnabled = this._offerActionsEnabled
        // finalizedPartIds covers both a picked winner and a part closed
        // without a contract — either way it no longer accepts new offers.
        const finalizedPartIds = [
            ...(this._procurement?.finalizedPartIds ?? []),
            ...(this._procurement?.closedWithoutContractPartIds ?? [])
        ]
        if (this._isProcurementFinalized || finalizedPartIds.includes(part._id.toString())) {
            section.finalized = YES
        }
        section.sectionDidChangeState = () => {
            if (!this._partSections.includes(section)) {
                return
            }
            this._updateProceedButton()
            this.setNeedsLayout()
            this._saveSessionSnapshot()
        }
        this._partSections.push(section)
        
        // Insert before the action buttons so they stay at the bottom
        this.proceedButton.removeFromSuperview()
        this.saveButton.removeFromSuperview()
        this.deleteDraftOfferButton?.removeFromSuperview()
        section.addedAsSubviewToView(this)
        this.proceedButton.addedAsSubviewToView(this)
        this.saveButton.addedAsSubviewToView(this)
        if (this.deleteDraftOfferButton) {
            this.deleteDraftOfferButton.addedAsSubviewToView(this)
        }
        
        return section
    }

    _saveSessionSnapshot() {
        if (this._formStateIsBeingApplied || !this._procurement) {
            return
        }
        const procurementId = this._procurement._id + ""
        const key = this._formSessionSnapshotKeyForProcurement(procurementId)
        if (!key) {
            return
        }
        const partOffers = this._partSections.flatMap(section => section.sessionSnapshots())
        if (partOffers.length === 0) {
            RHFormSessionSnapshotStore.removeSnapshotForKey(key)
            return
        }
        RHFormSessionSnapshotStore.saveSnapshotForKey<RHOfferFormSessionSnapshot>(
            { procurementId, partOffers },
            key
        )
    }

    _sessionSnapshotForProcurement(
        procurementId: string
    ): RHOfferFormSessionSnapshot | undefined {
        const key = this._formSessionSnapshotKeyForProcurement(procurementId)
        if (!key) {
            return undefined
        }
        const snapshot = RHFormSessionSnapshotStore.snapshotForKey<RHOfferFormSessionSnapshot>(key)
        if (!snapshot) {
            return undefined
        }
        if (snapshot.procurementId !== procurementId || !Array.isArray(snapshot.partOffers)) {
            RHFormSessionSnapshotStore.removeSnapshotForKey(key)
            return undefined
        }
        return snapshot
    }

    _formSessionSnapshotKeyForProcurement(procurementId: string): string | undefined {
        if (procurementId.length === 0) {
            return undefined
        }
        const userId = RHCore.sharedInstance.userProfile?._id?.toString() ?? ""
        if (userId.length === 0) {
            return undefined
        }

        let organizationId = "no-organization"
        const organization = RHCore.sharedInstance.userProfile?.organization
        if (organization) {
            organizationId = organization.toString()
            if (typeof organization !== "string" && organization._id) {
                organizationId = organization._id.toString()
            }
        }
        return userId + ":" + organizationId + ":offer:" + procurementId
    }
    
    private _updateProceedButton() {
        const hasAnyCard = this._partSections.some(section => section.offerCards.length > 0)
        const canSaveDraft = hasAnyCard && this._offerActionsEnabled &&
            !this._isSaving && !this._formStateIsBeingApplied
        this.saveButton.enabled = canSaveDraft
        this.proceedButton.enabled = canSaveDraft
    }
    
    private _clearSections() {
        this._partSections.forEach(s => s.removeFromSuperview())
        this._partSections = []
    }
    
    private _checkFinalized(): boolean {
        if (!this._procurement) { return false }
        return RHProcurementStatusPresentation.isProcurementInLifecycleCategory(this._procurement, "lopetatud") ||
            RHProcurementStatusPresentation.isProcurementInLifecycleCategory(this._procurement, "tuhistatud")
    }
    
    private _showErrorBanner(message: string) {
        this.errorBanner.configureBannerWithObject({
            alertType: RHAlertBannerView.type.Error,
            text: "⚠️ " + message
        })
        this.errorBanner.removeFromSuperview()
        this.proceedButton.removeFromSuperview()
        this.saveButton.removeFromSuperview()
        this.deleteDraftOfferButton?.removeFromSuperview()
        this.errorBanner.addedAsSubviewToView(this)
        this.proceedButton.addedAsSubviewToView(this)
        this.saveButton.addedAsSubviewToView(this)
        if (this.deleteDraftOfferButton) {
            this.deleteDraftOfferButton.addedAsSubviewToView(this)
        }
        this.setNeedsLayout()
    }
    
    private _hideErrorBanner() {
        this.errorBanner.removeFromSuperview()
    }
    
    async saveDraftAndProceedToConfirmation(
        buttonDisplayingSaveProgress: RHButton = this.proceedButton
    ): Promise<boolean> {
        const saveResult = await this._saveDraft(buttonDisplayingSaveProgress)
        if (!saveResult) { return NO }

        try {
            await this.didSaveDraftAndProceedToConfirm?.(
                saveResult.procurement,
                saveResult.savedDraftOffer,
                saveResult.offersByPartId
            )
            return YES
        }
        catch (error) {
            console.error("Error proceeding after draft save:", error)
            this._showErrorBanner(messageOfError(error) ?? "Viga salvestamisel")
            return NO
        }
    }

    async saveDraftAndNavigateToDashboard(): Promise<boolean> {
        const saveResult = await this._saveDraft(this.saveButton)
        if (!saveResult) { return NO }

        try {
            await this.didSaveDraftAndNavigateToDashboard?.()
            return YES
        }
        catch (error) {
            console.error("Error navigating after draft save:", error)
            this._showErrorBanner(messageOfError(error) ?? "Viga salvestamisel")
            return NO
        }
    }

    async saveDraft(buttonDisplayingSaveProgress: RHButton = this.saveButton): Promise<boolean> {
        const saveResult = await this._saveDraft(buttonDisplayingSaveProgress)
        if (!saveResult) { return NO }
        return YES
    }

    async _saveDraft(buttonDisplayingSaveProgress: RHButton): Promise<RHOfferDraftSaveResult | undefined> {
        const procurement = this._procurement
        if (!procurement || this._isSaving || this._formStateIsBeingApplied || !this._offerActionsEnabled) {
            return undefined
        }
        const saveGeneration = this._draftSaveGeneration
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        const isCurrentSave = () => saveGeneration === this._draftSaveGeneration &&
            companyId === RHCore.sharedInstance.companyAffiliationStore.activeCompanyId &&
            this._offerActionsEnabled
        
        this._hideErrorBanner()
        this._isSaving = true
        this.saveButton.enabled = NO
        this.proceedButton.enabled = NO
        buttonDisplayingSaveProgress.titleLabel.text = "Salvestamine..."
        
        try {
            // Awaited before any other validation so "Salvestamine..." stays
            // shown for as long as a file is still actually uploading, rather
            // than proceeding immediately and either silently omitting it
            // (still in flight) or ignoring that it never made it (failed).
            const uploadsSucceeded = await Promise.all(
                this._partSections.flatMap(section => section.pendingUploadCompletions())
            )
            if (!isCurrentSave()) {
                return undefined
            }
            if (uploadsSucceeded.some(succeeded => !succeeded)) {
                this._showErrorBanner(
                    "Faili üleslaadimine ebaõnnestus. Palun proovi uuesti."
                )
                return undefined
            }

            let invalidPriceCard: EnterProcurementItemOfferView | undefined
            for (const section of this._partSections) {
                for (const card of section.offerCards) {
                    if (
                        card.priceField.text.trim().length > 0 &&
                        !card.priceField.validate()
                    ) {
                        invalidPriceCard = card
                        break
                    }
                }
                if (invalidPriceCard) {
                    break
                }
            }
            if (invalidPriceCard) {
                this._showErrorBanner(
                    "Pakkumuse hinnaväljal peab olema nullist suurem või sellega võrdne arv."
                )
                invalidPriceCard.priceField.focus()
                return undefined
            }

            const partOffers = this._partSections.flatMap(s => s.partOfferInputs())
            
            if (partOffers.length === 0) {
                this._showErrorBanner("Palun sisesta vähemalt pakkumuse hind ja nõutud dokumendid")
                return undefined
            }
            
            for (const section of this._partSections) {
                const validationError = section.requiredDocumentValidationError()
                if (validationError) {
                    this._showErrorBanner(validationError)
                    return undefined
                }
            }
            
            const savedDraftOffer = await RHCore.sharedInstance.offerStore.saveDraftOffer(
                procurement._id + "",
                partOffers
            )
            if (!isCurrentSave()) {
                return undefined
            }
            this.discardCurrentSessionSnapshot()
            
            const savedPartOffers = savedDraftOffer.partOffers as RHProcurementPartOffer[]
            const offersByPartId = new Map<string, RHProcurementPartOffer[]>()
            for (const partOffer of savedPartOffers) {
                const key = partOffer.partId.toString()
                if (!offersByPartId.has(key)) { offersByPartId.set(key, []) }
                offersByPartId.get(key)!.push(partOffer)
            }
            
            return {
                procurement,
                savedDraftOffer,
                offersByPartId
            }
        }
        catch (error) {
            if (!isCurrentSave()) {
                return undefined
            }
            console.error("Error saving draft:", error)
            this._showErrorBanner(messageOfError(error) ?? "Viga salvestamisel")
            return undefined
        }
        finally {
            this._isSaving = false
            this.saveButton.titleLabel.text = RHMakeOfferViewTokens.saveButtonTitle
            this.proceedButton.titleLabel.text = RHMakeOfferViewTokens.proceedButtonTitle
            this._updateProceedButton()
        }
    }
    
    // ── Layout ────────────────────────────────────────────────────────────────
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(padding)
        
        if (this._isProcurementFinalized) {
            bounds
                .rectangleWithHeight(this.lockedMessageLabel)
                .assignedAsFrameOfView(this.lockedMessageLabel)
            return
        }
        
        let current = bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
        
        if (this._partSections.length > 0) {
            current = this.titleLabel.frame
                .rectangleForNextRow(padding)
                .framesByDistributingViewsAsColumn(this._partSections, padding * 1.5)
                .lastElement
        }
        
        if (this.errorBanner.superview === this) {
            current = current
                .rectangleForNextRow(padding, this.errorBanner)
                .assignedAsFrameOfView(this.errorBanner)
        }
        
        const actionButtonRowFrame = current.rectangleForNextRow(
            padding,
            RHMakeOfferViewTokens.actionButtonHeight
        )
        const actionButtons = [this.saveButton, this.proceedButton]
        if (this.deleteDraftOfferButton?.superview === this && !this.deleteDraftOfferButton.hidden) {
            actionButtons.push(this.deleteDraftOfferButton)
        }
        const buttonSlotWidth = (
            actionButtonRowFrame.width - RHSpacing.medium * (actionButtons.length - 1)
        ) / actionButtons.length
        const buttonWidths = actionButtons.map(button => [
            [
                button.titleLabel.intrinsicContentWidth(RHMakeOfferViewTokens.actionButtonHeight) +
                RHButtons.compactPrimaryContentPadding * 2 +
                RHSpacing.large,
                RHMakeOfferViewTokens.actionButtonMinWidth
            ].max(),
            RHMakeOfferViewTokens.actionButtonMaxWidth,
            buttonSlotWidth
        ].min())
        const actionButtonRowWidth = buttonWidths.reduce(
            (totalWidth, buttonWidth) => totalWidth + buttonWidth,
            RHSpacing.medium * (actionButtons.length - 1)
        )
        actionButtonRowFrame
            .rectangleWithWidth(actionButtonRowWidth, 0.5)
            .framesByDistributingViewsAsRow(actionButtons, RHSpacing.medium, buttonWidths)
    }
    
}
